/**
 * Base for dynamic block grips that open an editor instead of editing in place.
 *
 * A grip lives on the overlay canvas: it can be drawn and tapped, but it cannot show
 * a list of states or an angle dial. So it behaves as a button — the InteractionManager
 * turns a tap into start() immediately followed by end(), like DeleteLeaderHandle — and
 * publishes a request the Vue layer answers by opening the right editor.
 *
 * A real drag is treated as a cancelled tap.
 */

import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, Point3D } from "../types";
import { HandlePosition } from "../types";
import { AbstractHandle } from "./AbstractHandle";
import { dynPickBus, type DynBlockPickKind } from "../../blocks/dynPickBus";
import { Logger } from "../../../logger";

/** Movement (in screen pixels) above which the gesture is a drag, not a tap. */
const CLICK_THRESHOLD_PX = 5;

export abstract class DynPickHandle extends AbstractHandle {
  readonly position = HandlePosition.DynProperty;

  /** These grips are buttons: nothing to snap to. */
  snapOptions = { acceptOrtho: false, acceptPolar: false, acceptObject: false };

  /** Editor the Vue layer must open on tap. */
  protected abstract readonly kind: DynBlockPickKind;

  private startPosition: Point3D | null = null;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    protected readonly entityHandle: string,
    protected readonly propertyName: string,
    /** Empty when the property is a free scalar. */
    protected readonly allowedValues: string[],
    /** Value at the time the handle was created. */
    protected readonly currentValue: string,
    worldPosition: Point3D,
  ) {
    super(viewer, history);
    this.worldPosition = { ...worldPosition };
  }

  start(context: TransformContext): void {
    if (this.isTransforming) return;
    this.isTransforming = true;
    this.startPosition = { ...context.worldPoint };
  }

  ongoing(): void {
    // Nothing to preview: the editor opens on release.
  }

  end(context: TransformContext): void {
    if (!this.isTransforming) return;
    this.isTransforming = false;

    const wasClick = this.isClick(context);
    this.startPosition = null;
    if (!wasClick) return;

    Logger.info(
      `DynPickHandle : open ${this.kind} editor for ${this.propertyName}`,
    );
    dynPickBus.emit({
      kind: this.kind,
      blockHandle: this.entityHandle,
      propertyName: this.propertyName,
      currentValue: this.currentValue,
      allowedValues: this.allowedValues,
      screenPosition: this.viewer.worldToScreen(this.worldPosition),
    });
  }

  cancel(): void {
    if (!this.isTransforming) return;
    this.isTransforming = false;
    this.startPosition = null;
  }

  private isClick(context: TransformContext): boolean {
    if (!this.startPosition) return true;
    const dx = context.worldPoint.x - this.startPosition.x;
    const dy = context.worldPoint.y - this.startPosition.y;
    const moved = Math.sqrt(dx * dx + dy * dy);
    const threshold = Math.abs(
      this.viewer.screenDistanceToWorld(CLICK_THRESHOLD_PX),
    );
    return moved <= threshold;
  }
}
